import { isReference } from '@apollo/client';
import type { FetchResult, StoreObject } from '@apollo/client';

import { isDevelopment } from '@constants/env';

import { useResponseHandler } from '@hooks/useResponseHandler';
import { useSession } from '@hooks/useSession';

import type { GetFhirRecordParams } from '@models/fhir-resource';
import { ProcedureRecord } from '@models/procedure';

import { parseArgs, type CachedRecordList } from '@utils/apollo';

import type { GetPatientHealthSummaryRecordsQuery } from 'generated/patient-record.generated';
import { GetPatientHealthSummaryRecordsDocument } from 'generated/patient-record.generated';
import type {
  UpdateProcedureRecordMutationVariables,
  PostProcedureRecordMutationVariables,
  DeleteProcedureRecordMutationVariables,
  DeleteProcedureRecordMutation,
} from 'generated/procedure.generated';
import {
  useListProceduresQuery,
  useGetProcedureQuery,
  usePostProcedureRecordMutation,
  useUpdateProcedureRecordMutation,
  useDeleteProcedureRecordMutation,
} from 'generated/procedure.generated';

export const useGetProcedures = () => {
  const { handleError } = useResponseHandler();
  const {
    state: { activeProfile },
  } = useSession();
  const patient_id = activeProfile?.patient_id ?? '';
  const response = useListProceduresQuery({
    variables: { input: { patient: patient_id, count: 1500, page: 1 } },
    skip: !patient_id,
  });
  const { data, error } = response;
  handleError(error);
  const records = (data?.listProcedures.data ?? []).map(
    (r) => new ProcedureRecord(r)
  );
  return { ...response, data: records };
};

export const useGetProcedure = (params: GetFhirRecordParams) => {
  const { handleError } = useResponseHandler();
  const {
    state: { activeProfile },
  } = useSession();
  const patient_id = activeProfile?.patient_id ?? '';
  const response = useGetProcedureQuery({
    variables: { input: { record_id: params.record_id, patient_id } },
    skip:
      !patient_id ||
      !params.record_id ||
      params.record_id === 'add' ||
      Boolean(params.skip),
  });
  const { data, error } = response;
  handleError(error);
  const record = data?.getProcedure;
  return { ...response, data: record ? new ProcedureRecord(record) : null };
};

export function useCreateProcedure(): [
  (
    params: PostProcedureRecordMutationVariables
  ) => Promise<FetchResult<ProcedureRecord>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] = usePostProcedureRecordMutation();
  const loading = baseLoading;

  async function createProcedure(params: PostProcedureRecordMutationVariables) {
    const response = await mutate({
      variables: { ...params },
      update: (cache, { data: response }) => {
        if (!response?.postProcedureRecord) {
          return;
        }
        const storeObject =
          response.postProcedureRecord as unknown as StoreObject;
        const { procedure } = params.input;
        try {
          cache.modify<{ listProcedures: CachedRecordList }>({
            fields: {
              listProcedures: (existing, { storeFieldName, toReference }) => {
                if (isReference(existing)) {
                  return existing;
                }
                const parsed = parseArgs(storeFieldName);
                if (parsed?.patient === procedure.subject.reference) {
                  return {
                    ...existing,
                    data: [...existing.data, toReference(storeObject)],
                  };
                }
                return existing;
              },
            },
          });

          cache.updateQuery<GetPatientHealthSummaryRecordsQuery>(
            {
              query: GetPatientHealthSummaryRecordsDocument,
              variables: {
                input: {
                  patient: procedure.subject.reference,
                },
                procedure: true,
              },
            },
            (data) => ({
              getPatientHealthSummaryRecords: {
                procedure: [
                  ...(data?.getPatientHealthSummaryRecords.procedure ?? []),
                  response.postProcedureRecord,
                ],
              },
            })
          );
        } catch (e) {
          if (isDevelopment) {
            // eslint-disable-next-line no-console
            console.error(e);
          }
        }
      },
    });
    const createdProcedure = response.data?.postProcedureRecord;
    return {
      ...response,
      data: createdProcedure
        ? new ProcedureRecord(createdProcedure)
        : undefined,
    };
  }
  return [createProcedure, loading];
}

export function useUpdateProcedure(): [
  (
    params: UpdateProcedureRecordMutationVariables
  ) => Promise<FetchResult<ProcedureRecord>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] = useUpdateProcedureRecordMutation();
  const loading = baseLoading;

  async function updateProcedure(
    params: UpdateProcedureRecordMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
    });
    const updatedProcedure = response.data?.updateProcedureRecord;
    return {
      ...response,
      data: updatedProcedure
        ? new ProcedureRecord(updatedProcedure)
        : undefined,
    };
  }
  return [updateProcedure, loading];
}

export function useDeleteProcedure(): [
  (
    params: DeleteProcedureRecordMutationVariables
  ) => Promise<FetchResult<DeleteProcedureRecordMutation>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] = useDeleteProcedureRecordMutation();
  const loading = baseLoading;

  async function deleteProcedure(
    params: DeleteProcedureRecordMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
      update: (cache, { data: response }) => {
        if (!response?.deleteProcedureRecord) {
          return;
        }
        const { procedure_id: id } = params.input;
        try {
          const normalizedId = cache.identify({
            id,
            __typename: 'ProcedureModel',
          });
          cache.evict({ id: normalizedId });
          cache.gc();
        } catch (e) {
          if (isDevelopment) {
            // eslint-disable-next-line no-console
            console.error(e);
          }
        }
      },
    });
    return response;
  }
  return [deleteProcedure, loading];
}
